/* FT GUYS - worlds grid: auto-fit + live ink fill.

   TWO JOBS, ONE PIXEL READ.

   1. AUTO-FIT. The lockups were drawn at different scales inside their artboards,
      so cropping equalises the frame but not the artwork. Each image is scanned for
      its ink bounding box, and every tile is scaled so the wordmarks render at the
      same width and shifted so the ink sits centred. Measured, not eyeballed, and
      it still holds when a sixth world is added.

   2. LIVE INK FILL. The white-over-orange split is baked into the PNGs. To animate
      it, the colour has to come off the artwork - which normally means re-exporting
      as transparent files, because CSS luminance masking is not reliable across
      browsers and these images are opaque.

      Instead the mask is BUILT HERE: the image is read once, luminance is converted
      to alpha (dark ground -> transparent, bright wordmark -> opaque) and the result
      becomes a PNG data URL. That is a real alpha mask, so plain mask-image works
      everywhere. Two layers are then painted through it - ink white underneath,
      accent orange above - and the orange one rises on scroll-in.

   WHEN THIS RUNS: on approach, not at page load. Five canvas reads at ~560px wide,
   each decoding an image and walking every pixel twice, is real main-thread work -
   and the grid sits below the hero, so none of it is needed during first paint.
   An IntersectionObserver with a 600px margin starts the analysis just before the
   grid arrives, which is far enough ahead that the fill is ready when it lands.

   Threshold is a ramp, not a cutoff: alpha eases in between LO and HI so letter
   edges keep their antialiasing. A hard threshold would give jagged type.

   Needs CORS to read pixels; Shopify's CDN allows it. Any failure leaves that tile
   showing the original image untouched - it degrades, it does not break.
*/
(function () {
  'use strict';

  var INK = 46;          // luminance above this counts as artwork, for measuring
  var LO = 26, HI = 96;  // alpha ramp for the generated mask
  var MAX_FILL = 0.88;
  var MIN_S = 0.55, MAX_S = 2.2;

  function analyse(url, cb) {
    var im = new Image();
    im.crossOrigin = 'anonymous';
    im.onerror = function () { cb(null); };
    im.onload = function () {
      try {
        // big enough for a crisp mask, small enough to stay cheap
        var w = Math.min(560, im.naturalWidth);
        var h = Math.max(1, Math.round(im.naturalHeight * (w / im.naturalWidth)));
        var c = document.createElement('canvas');
        c.width = w; c.height = h;
        var ctx = c.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(im, 0, 0, w, h);
        var img = ctx.getImageData(0, 0, w, h);
        var d = img.data;

        var minX = w, maxX = -1, minY = h, maxY = -1;
        for (var i = 0; i < d.length; i += 4) {
          // Rec. 601 luma - orange must weigh the same as white when measuring
          var lum = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];

          if (lum > INK && d[i + 3] > 20) {
            var p = i / 4, x = p % w, y = (p / w) | 0;
            if (x < minX) minX = x;
            if (x > maxX) maxX = x;
            if (y < minY) minY = y;
            if (y > maxY) maxY = y;
          }

          // luminance -> alpha, eased across the ramp so edges stay antialiased
          var a = (lum - LO) / (HI - LO);
          a = a < 0 ? 0 : (a > 1 ? 1 : a);
          d[i] = 255; d[i + 1] = 255; d[i + 2] = 255;
          d[i + 3] = Math.round(a * 255);
        }
        if (maxX < 0) { cb(null); return; }

        ctx.putImageData(img, 0, 0);

        cb({
          mask: c.toDataURL('image/png'),
          fw: (maxX - minX + 1) / w,
          cy: ((minY + maxY + 1) / 2) / h
        });
      } catch (e) { cb(null); }
    };
    im.src = url;
  }

  function paint(media, maskUrl) {
    ['base', 'fill'].forEach(function (kind) {
      var el = document.createElement('span');
      el.className = 'ft-worlds__paint ft-worlds__paint--' + kind;
      el.setAttribute('aria-hidden', 'true');
      el.style.webkitMaskImage = 'url(' + maskUrl + ')';
      el.style.maskImage = 'url(' + maskUrl + ')';
      media.appendChild(el);
    });
    media.classList.add('is-painted');
  }

  function apply(cells) {
    var widths = cells.map(function (c) { return c.m.fw; }).sort(function (a, b) { return a - b; });
    var median = widths[Math.floor(widths.length / 2)];
    var target = Math.min(median, MAX_FILL);

    cells.forEach(function (c) {
      var s = Math.max(MIN_S, Math.min(MAX_S, target / c.m.fw));
      var shift = ((0.5 - c.m.cy) * 100) / s;
      c.cell.style.setProperty('--ft-auto-zoom', s.toFixed(4));
      c.cell.style.setProperty('--ft-shift', shift.toFixed(2) + '%');
      paint(c.media, c.m.mask);
    });
  }

  // the actual work: only ever called once the grid is close to view
  function run(cells) {
    var out = [], pending = 0;

    cells.forEach(function (cell) {
      var img = cell.querySelector('img');
      var media = cell.querySelector('.ft-worlds__media');
      if (!img || !media || media.classList.contains('is-painted')) return;
      pending++;
      var go = function () {
        analyse(img.currentSrc || img.src, function (m) {
          if (m) out.push({ cell: cell, media: media, m: m });
          if (--pending === 0 && out.length) apply(out);
        });
      };
      if (img.complete && img.naturalWidth) go();
      else img.addEventListener('load', go, { once: true });
    });
  }

  function init() {
    var grids = Array.prototype.slice.call(document.querySelectorAll('.ft-worlds__grid'));
    if (!grids.length) {
      // markup without the wrapper: fall back to the flat cell list
      var flat = Array.prototype.slice.call(document.querySelectorAll('.ft-worlds__cell'));
      if (flat.length) run(flat);
      return;
    }

    grids.forEach(function (grid) {
      if (grid.dataset.ftFitWired) return;
      grid.dataset.ftFitWired = '1';
      var cells = Array.prototype.slice.call(grid.querySelectorAll('.ft-worlds__cell'));
      if (!cells.length) return;

      if (!('IntersectionObserver' in window)) { run(cells); return; }

      // 600px ahead: far enough that the fill is ready before the grid lands,
      // late enough that none of it competes with first paint
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          io.disconnect();
          run(cells);
        });
      }, { rootMargin: '600px 0px' });
      io.observe(grid);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
